import moment from 'moment-timezone';

import {ChatType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {ChatFileUpload, UserContext} from 'llm/chat-library/ChatLibraryTypes';
import {AgentMode} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import * as DataCaptureDAO from 'llm/data-capture/DataCaptureDAO';
import * as DeepResearchDAO from 'llm/deep-research/DeepResearchDAO';
import SidepanelStore from 'nav/sidepanel/SidepanelStore';
import WorkspaceStore from 'sidebar/WorkspaceStore';
import * as Net from 'util/Net';
import {assertNever} from 'util/Types';

export async function createChatSession(chatType: ChatType): Promise<string> {
  const response = await Net.postJSONPromise({
    url: '/1/api/chats',
    data: {type: chatType},
  });
  return response.id;
}

/**
 * Adds a user message to an existing chat.
 *
 * This is an aspirational DAO, but currently our different agent implementations are vastly different,
 * where this intends to provide a unified interface that papers over the differences.
 *
 * Notably:
 *   - ASK / DATA_CAPTURE need a chat to exist before posting a message, while DEEP_RESEARCH does not.
 *   - DEEP_RESEARCH will quickly post a message synchronously and resposne happens async via celery.
 *   - DATA_CAPTURE will post a message asynchronously in celery.
 *   - ASK will post and often repond to a message synchronously, where the async task will no-op.
 *
 * For the best UX, caller should use optimistic rendering of the posted message, and then show loading state
 * while polling for the response. The promise may reject immediately with a 4xx if the message failed
 * to post.

* @param chatId The chat to post to. If `null`, a new chat will be created.
 * @param agentMode - each
 * @param message - components of a DisplayMessageChatEvent (DisplayTextBlock / DisplayFileBlock).
 * @param options - publicDataEnabled - only relevant for DEEP_RESEARCH.
 * @returns Promise that resolves as soon as the chat and initial message to backend has been initiated.
 */
export async function sendMessage(
  chatId: string,
  agentMode: AgentMode,
  message: {
    text: string;
    fileUploads: Array<ChatFileUpload>;
  },
  options: {publicDataEnabled: boolean}
): Promise<void> {
  switch (agentMode) {
    case AgentMode.DEEP_RESEARCH: {
      await DeepResearchDAO.initiateDeepResearchMessage(
        chatId,
        message.text /* message */,
        options.publicDataEnabled /* publicDataEnabled */,
        message.fileUploads /* fileUploads */
      );
      return;
    }
    case AgentMode.ASK: {
      const activeIds = [];
      if (!SidepanelStore.getIsExpanded() && WorkspaceStore.activeId) {
        activeIds.push(WorkspaceStore.activeId);
      }

      // Capture user context for ASK mode
      const context: UserContext = {
        ids: activeIds,
        // intentionally en-US to match the rest of our prompting
        userLocalTime: new Date().toLocaleString('en-US', {
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: 'numeric',
          minute: 'numeric',
        }),

        userTimezone: moment.tz.guess(),
      };
      await DeepResearchDAO.initiateAskMessage(chatId, message.text, context, message.fileUploads);
      return;
    }
    case AgentMode.DATA_CAPTURE: {
      await DataCaptureDAO.runDataCaptureAssistant(chatId, message.text, message.fileUploads);
      return;
    }
    default:
      throw new Error(`Unknown agent mode: ${assertNever(agentMode)}`);
  }
}

/**
 * Get the download URL for a file uploaded to a chat session.
 */
export function getChatFileDownloadUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:download`;
}

/**
 * Get the preview URL for a file uploaded to a chat session.
 */
export function getChatFilePreviewUrl(chatSessionId: string, externalFileId: string): string {
  return `/1/api/chats/${chatSessionId}/uploaded-files/${externalFileId}:preview`;
}
